import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from "@angular/core";

@Component({
  selector: "app-key-account-tree",
  templateUrl: "./key-account-tree.component.html",
  styleUrl: "./key-account-tree.component.scss",
})
export class KeyAccountTreeComponent implements OnInit, OnChanges {
  @Input() treeNodeKeyAccountList: any[] = [];
  @Output() onSelectedKeyAccountsEmit = new EventEmitter<any>();
  @Input() hasAllowMulipleSelection: boolean = true;

  selectedNodeKeyAccount: any[] = [];
  selectAll: boolean = false;

  // Custom filter properties
  searchValue: string = "";
  filteredTreeNodeList: any[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filteredTreeNodeList = [...this.treeNodeKeyAccountList];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["treeNodeKeyAccountList"]) {
      this.filteredTreeNodeList = [...this.treeNodeKeyAccountList];
      this.applyFilter();
    }

    if (changes["hasAllowMulipleSelection"]?.currentValue === false) {
      this.hasAllowMulipleSelection =
        changes["hasAllowMulipleSelection"].currentValue;
    }
  }

  // Public method to clear the selection (can be called from parent component)
  clearSelection(): void {
    this.selectedNodeKeyAccount = [];
    this.selectAll = false;
    this.searchValue = "";
    this.filteredTreeNodeList = [...this.treeNodeKeyAccountList];
  }

  // Method to handle custom filter input changes
  onCustomFilterChange(searchText: string): void {
    this.searchValue = searchText;
    this.applyFilter();
  }

  // Apply filter to the tree nodes
  private applyFilter(): void {
    const value = this.searchValue?.trim().toLowerCase();

    if (!value) {
      // Avoid deep clone unless mutation is happening elsewhere
      this.filteredTreeNodeList = this.treeNodeKeyAccountList;
      this.updateTreeSelection();
      return;
    }

    this.filteredTreeNodeList = this.filterTreeNodes(
      this.treeNodeKeyAccountList,
      value
    );

    this.updateTreeSelection();
  }

  // Helper method to recursively filter tree nodes based on label, parent label, or type
  private filterTreeNodes(nodes: any[], filterValue: string): any[] {
    const result: any[] = [];

    for (const node of nodes) {
      const label = node.label?.toLowerCase() ?? "";
      const parentLabel = node.data?.parentLabel?.toLowerCase() ?? "";
      const type = node.data?.type?.toLowerCase() ?? "";

      const isMatch =
        label.includes(filterValue) ||
        parentLabel.includes(filterValue) ||
        type.includes(filterValue);

      let children: any[] = [];

      if (node.children?.length) {
        children = this.filterTreeNodes(node.children, filterValue);
      }

      if (isMatch || children.length) {
        result.push({
          ...node,
          children: children.length ? children : node.children,
          expanded: isMatch || children.length > 0,
        });
      }
    }

    return result;
  }

  // Method to update tree selection after filtering to ensure selected nodes are still selected if they exist in the filtered list
  private updateTreeSelection(): void {
    if (!this.selectedNodeKeyAccount?.length) return;

    const keySet = new Set(this.selectedNodeKeyAccount.map((n) => n.key));

    const updatedSelection = this.findNodesByKeys(
      this.filteredTreeNodeList,
      keySet
    );

    // Clear + restore (PrimeNG requirement)
    this.selectedNodeKeyAccount = [];

    Promise.resolve().then(() => {
      this.selectedNodeKeyAccount = updatedSelection;

      this.expandParentsOfSelectedNodes(keySet);
      this.cdr.detectChanges();
    });
  }

  // Helper method to find nodes by keys in the current filtered tree
  private findNodesByKeys(nodes: any[], keySet: Set<string>): any[] {
    const found: any[] = [];
    const stack = [...nodes];

    while (stack.length) {
      const node = stack.pop();

      if (keySet.has(node.key)) {
        found.push(node);
      }

      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return found;
  }

  // Expand parents efficiently
  private expandParentsOfSelectedNodes(keySet: Set<string>): void {
    this.markParentsAsExpanded(this.filteredTreeNodeList, keySet);
  }

  // Helper method for recursively mark parent nodes as expanded if any child is selected
  private markParentsAsExpanded(nodes: any[], keySet: Set<string>): boolean {
    let hasSelected = false;

    for (const node of nodes) {
      let childSelected = false;

      if (node.children?.length) {
        childSelected = this.markParentsAsExpanded(node.children, keySet);
      }

      const isSelected = keySet.has(node.key);

      if (isSelected || childSelected) {
        node.expanded = true;
        hasSelected = true;
      }
    }

    return hasSelected;
  }

  // Handle radio button click explicitly so it triggers the same emission as tree node select
  onRadioButtonClick(node: any, event: any): void {
    if (event?.originalEvent?.stopPropagation) {
      event.originalEvent.stopPropagation();
    }
    this.selectedNodeKeyAccount = node;
    this.onSelectionChange({ node });
  }

  // Prevent deselection in single-select mode — PrimeNG clears the selection
  // in the same tick, so we restore it asynchronously after the cycle completes
  onNodeUnselect(event: any): void {
    if (!this.hasAllowMulipleSelection) {
      const reselected = event.node;
      Promise.resolve().then(() => {
        this.selectedNodeKeyAccount = reselected;
        this.cdr.detectChanges();
      });
      return;
    }
    this.onSelectionChange(event);
  }

  // Method to handle selection change in the tree select component
  // and emit the selected key accounts to the parent component
  onSelectionChange(event: any): void {
    // The selectedNodeKeyAccount is automatically updated by [(selection)] binding
    // event.node contains the node that was just selected/unselected

    // Expand parents when a key account is selected
    if (event.node && this.isNodeSelected(event.node)) {
      this.expandParentsOfNode(event.node);
    }

    // Emit all currently selected nodes to parent
    this.onSelectedKeyAccountsEmit.emit({
      selectedNodes: this.selectedNodeKeyAccount,
      changedNode: event.node,
      eventType: event.node
        ? this.isNodeSelected(event.node)
          ? "select"
          : "unselect"
        : null,
    });
  }

  // Expand all parent nodes of a specific node
  private expandParentsOfNode(node: any): void {
    const nodePath = this.findNodePath(this.filteredTreeNodeList, node.key);
    nodePath.forEach((pathNode) => {
      pathNode.expanded = true;
    });
  }

  // Find the path to a node (returns array of nodes from root to target)
  private findNodePath(
    nodes: any[],
    targetKey: string,
    path: any[] = []
  ): any[] {
    for (const node of nodes) {
      const currentPath = [...path, node];

      if (node.key === targetKey) {
        return currentPath;
      }

      if (node.children && node.children.length > 0) {
        const foundPath = this.findNodePath(
          node.children,
          targetKey,
          currentPath
        );
        if (foundPath.length > 0) {
          return foundPath;
        }
      }
    }

    return [];
  }

  // Helper method to check if a node is currently selected
  private isNodeSelected(node: any): boolean {
    if (!this.selectedNodeKeyAccount) return false;
    // In single selection mode, selectedNodeKeyAccount is a plain object, not an array
    if (Array.isArray(this.selectedNodeKeyAccount)) {
      return this.selectedNodeKeyAccount.some(
        (selected) => selected.key === node.key
      );
    }
    return (this.selectedNodeKeyAccount as any).key === node.key;
  }

  // Method to handle select all change event and emit the selected key accounts to the parent component
  onSelectAllChange(event: any) {
    if (event.checked) {
      this.selectedNodeKeyAccount = this.getAllNodes(
        this.treeNodeKeyAccountList
      );
      this.selectAll = true;
    } else {
      this.selectedNodeKeyAccount = [];
      this.selectAll = false;
    }

    // Emit with metadata to indicate this is a select-all action
    this.onSelectedKeyAccountsEmit.emit({
      selectedNodes: this.selectedNodeKeyAccount,
      changedNode: null,
      eventType: event.checked ? "select-all" : "unselect-all",
    });
  }

  // Helper method to get all nodes from the tree structure
  private getAllNodes(nodes: any[]): any[] {
    let allNodes: any[] = [];
    nodes.forEach((node) => {
      allNodes.push(node);
      if (node.children && node.children.length > 0) {
        allNodes = allNodes.concat(this.getAllNodes(node.children));
      }
    });
    return allNodes;
  }
}
